import {
  Autocomplete,
  Box,
  FilterOptionsState,
  FormControl,
  FormLabel,
  Grid,
  ListItem,
  OutlinedInput,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import ManagementUpdateHeader from "../ManagementsUpdateHeader/ManagementsUpdateHeader";
import { Trans, useTranslation } from "react-i18next";
import CupsCifSelector from "../../CupsCifSelector/CupsCifSelector";
import {
  AddressUpdateData,
  NewAddressData,
  UpdateShippingAddressRequestBody,
} from "../../../core/models/managementsUpdate/managementUpdateAddress/managementUpdateAddress.model";
import { useCallback, useContext, useEffect, useState } from "react";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import theme from "../../../muiTheme";
// @ts-ignore: missing type declarations for style side-effect import
import "./ManagementUpdateAddress.scss";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import { useSearchParams } from "react-router-dom";
import ManagementsRequestState from "../../Managements/ManagementsRequestState/ManagementsRequestState";
import { Typography } from "@mui/material";
import { ManagementUpdateCup } from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import streetsJSON from "../../../assets/data/streetTypes.json";
import {
  formatUpdateShippingAddressRequestBody,
  initialNewAddressData,
} from "../../../utils/components/managementsUpdate/managementsUpdateAddress/managementsUpdateAddress.util";
import { updateShippingAddress } from "../../../core/services/managementsUpdate/managementsUpdate.service";
import {
  CitiesResponse,
  ManagementCupsInfo,
  MunicipalitiesResponse,
  ProvincesResponse,
  RequestStateDescriptionData,
} from "../../../core/models/managements/managements.model";
import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_RESOLVED,
  MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT,
} from "../../../core/constants/constants";
import {
  formatOwnerDataCup,
  getManagementsRequestTypeData,
} from "../../../utils/components/managements/managements.util";
import { initialOwnerDataCup } from "../../../utils/ownerData/ownerData.util";
import ManagementsCancelProcess from "../../Managements/ManagementsCancelProcess/ManagementsCancelProcess";
import React from "react";
import {
  getCities,
  getMunicipalities,
  getProvinces,
} from "../../../core/services/managements/managements.service";

type TooltipPlacement =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "bottom-end"
  | "bottom-start"
  | "left-end"
  | "left-start"
  | "right-end"
  | "right-start"
  | "top-end"
  | "top-start"
  | undefined;

interface TooltipStyles {
  width: string;
  placement: TooltipPlacement;
}

const managementAddressUpdateData: AddressUpdateData[] = [
  {
    id: "name",
    label: "Nombre",
    required: false,
  },
  {
    id: "province",
    label: "Población",
    required: true,
  },
  {
    id: "municipality",
    label: "Municipio",
    required: true,
  },
  {
    id: "city",
    label: "Ciudad",
    required: true,
  },
  {
    id: "streetType",
    label: "Tipo de vía",
    required: true,
  },
  {
    id: "address",
    label: "Dirección",
    required: true,
  },
  {
    id: "number",
    label: "Número",
    type: "number",
    required: true,
  },
  {
    id: "zip",
    label: "Código postal",
    type: "number",
    required: true,
  },
  {
    id: "supplementary",
    label: "Aclarador",
    required: false,
  },
];

const ManagementUpdateAddress = () => {
  const { t } = useTranslation();
  const { ownerData, userData, isAgent } = useContext(TotalEnergiesContext);
  const [searchParms] = useSearchParams();
  const idCups = searchParms.get("idCups") || "";
  const [step, setStep] = useState<number>(idCups.length ? 0 : -1);
  const [selectedCups, setSelectedCups] = useState<ManagementUpdateCup[]>([]);
  const [newAddressData, setNewAddressData] = useState<NewAddressData>(
    initialNewAddressData,
  );
  const [managementCupstInfo, setManagementCupsInfo] = useState<
    ManagementCupsInfo[] | undefined
  >(undefined);
  const [requestState, setRequestState] = useState<string>("");
  const [provinces, setProvinces] = useState<ProvincesResponse[]>([]);
  const [municipalities, setMunicipalities] = useState<ProvincesResponse[]>([]);
  const [municipalitySelected, setMunicipalitySelected] =
    useState<ProvincesResponse | null>(null);
  const [isMunicipalityDisabled, setIsMunicipalityDisabled] = useState(true);
  const [cities, setCities] = useState<CitiesResponse[]>([]);
  const [citySelected, setCitySelected] = useState<CitiesResponse | null>(null);
  const [isCityDisabled, setIsCityDisabled] = useState(true);
  const [provinceId, setProvinceId] = useState<number | null>(null);
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>({
      title: "",
      description: "",
    });
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const isLargeViewport = useMediaQuery("(min-width: 1495px)");

  const isSmallViewport = useMediaQuery("(max-width: 766px)");
  const isMediumViewport = useMediaQuery("(max-width: 1038px)");

  const streetsArray = Object.values(streetsJSON);
  const streets = [
    ...new Set(streetsArray.map(({ full, value }) => ({ full, value }))),
  ].sort(({ full: a }, { full: b }) => a.localeCompare(b));
  const ownerDataCup =
    ownerData?.cups.find(
      ({ idCups: ownerDataIdCups }) => ownerDataIdCups.toString() === idCups,
    ) || initialOwnerDataCup;
  const areAllRequiredValuesAssigned = useCallback(
    (
      newAddressData: NewAddressData,
      managementAddressUpdateData: AddressUpdateData[],
    ): boolean => {
      const requiredFields = managementAddressUpdateData
        .filter((field) => field.required)
        .map((field) => field.id);

      return requiredFields.every((fieldId) => newAddressData[fieldId] !== "");
    },
    [],
  );

  useEffect(() => {
    if (userData) {
      getProvinces(userData.token).then((provinces) => {
        setProvinces(provinces);
      });
    }
  }, [userData]);

  const handleProvinceChange = (
    event: React.SyntheticEvent,
    value: ProvincesResponse | null,
  ) => {
    if (userData) {
      setMunicipalities([]);
      setMunicipalitySelected(null);
      setIsMunicipalityDisabled(true);
      setCities([]);
      setCitySelected(null);
      setIsCityDisabled(true);
      setProvinceId(value ? value.id : null);
      setNewAddressData((prevNewAddressData) => ({
        ...prevNewAddressData,
        province: value ? value.id.toString() : "",
        municipality: "",
        city: "",
      }));

      if (value != null) {
        getMunicipalities(userData.token, value.id)
          .then((municipalities) => {
            setMunicipalities(municipalities);
          })
          .finally(() => {
            setIsMunicipalityDisabled(false);
          });
      }
    }
  };

  const handleMunicipalityChange = (
    event: React.SyntheticEvent,
    value: MunicipalitiesResponse | null,
  ) => {
    if (value != null && provinceId !== null && userData) {
      setMunicipalitySelected(value);
      setNewAddressData((prevNewAddressData) => ({
        ...prevNewAddressData,
        municipality: value.id.toString(),
      }));

      setCities([]);
      setCitySelected(null);
      setIsCityDisabled(true);

      getCities(userData.token, provinceId, value.id)
        .then((cities) => {
          setCities(cities);
        })
        .finally(() => {
          setIsCityDisabled(false);
        });
    } else {
      setMunicipalitySelected(null);
      setCities([]);
      setCitySelected(null);
      setIsCityDisabled(true);
      setNewAddressData((prevNewAddressData) => ({
        ...prevNewAddressData,
        municipality: "",
        city: "",
      }));
    }
  };

  useEffect(() => {
    if (idCups.length && ownerData) {
      setSelectedCups([formatOwnerDataCup(ownerDataCup)]);
    }
  }, [idCups, ownerData, ownerDataCup]);

  useEffect(() => {
    setIsButtonDisabled(
      !areAllRequiredValuesAssigned(
        newAddressData,
        managementAddressUpdateData,
      ),
    );
  }, [areAllRequiredValuesAssigned, newAddressData]);

  const handleFilterOptions = (
    options: ProvincesResponse[],
    { inputValue }: FilterOptionsState<ProvincesResponse>,
  ) => {
    const value = inputValue.trim().toLowerCase();

    if (value.length > 1) {
      return options.filter((option) =>
        option.name.toLowerCase().includes(value),
      );
    }
    return options;
  };

  const handleSubmit = () => {
    const successfulRequests: ManagementCupsInfo[] = [];
    setIsLoading(true);

    selectedCups.forEach((cup) => {
      const updateShippingAddressRequestBody: UpdateShippingAddressRequestBody =
        {
          ...formatUpdateShippingAddressRequestBody(
            cup.cupsCode,
            newAddressData.name ?? cup.supplyAddress,
            newAddressData,
          ),
          idCiudad: citySelected ? citySelected.id.toString() : "",
        };

      setStep(1);

      updateShippingAddress(
        updateShippingAddressRequestBody,
        userData?.token || "",
      )
        .then((response) => {
          setRequestState(MANAGEMENTS_STATE_RESOLVED);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT,
              MANAGEMENTS_STATE_RESOLVED,
              isAgent ?? false,
            ),
          );

          const address = ownerData?.cups.find(
            (item) => item.codeCups === response[0].Cups,
          )?.address;

          successfulRequests.push({
            cupsCode: response[0].Cups,
            address: address ?? "",
            environment: response[0].EntornoBD,
          });

          setStep(3);
        })
        .catch(() => {
          setRequestState(MANAGEMENTS_STATE_ERROR);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false,
            ),
          );
        })
        .finally(() => {
          if (successfulRequests.length) {
            setManagementCupsInfo(successfulRequests);
          }

          setIsLoading(false);
        });
    });
  };

  const renderTooltipBreakpointsStyles = useCallback(
    (property: keyof TooltipStyles) => {
      const tooltipStyles: TooltipStyles = {
        width: "200px",
        placement: "right-start",
      };

      if (isLargeViewport || isMediumViewport) {
        tooltipStyles.width = "400px";
      }

      if (isMediumViewport) {
        tooltipStyles.placement = "top";
      }

      if (isSmallViewport) {
        tooltipStyles.placement = "top-end";
        tooltipStyles.width = "450px";
      }

      return tooltipStyles[property];
    },
    [isSmallViewport, isMediumViewport, isLargeViewport],
  );

  return (
    <Box component="section" className="management-update-address">
      <ManagementUpdateHeader
        title={"Actualizar dirección de correspondencia"}
        step={step}
        cupsSelected={selectedCups}
        ownerDataCup={ownerDataCup}
      />
      <Stack
        component="article"
        direction="column"
        justifyContent="center"
        alignItems="center"
        paddingTop={5}
        bgcolor={{ md: theme.palette.grey[50] }}
      >
        {step === -1 && (
          <CupsCifSelector
            withContainer
            setUpdateCups={(selectedCups) => {
              setSelectedCups(selectedCups);
              setStep(0);
              window.scrollTo({ top: 0, behavior: "auto" });
            }}
            selectedCups={selectedCups}
            setSelectedCups={setSelectedCups}
            title={"Gestiones.¿Dónde quieres aplicar el cambio?"}
            description={"Gestiones.El cambio se solicitará"}
          />
        )}
        {step === 0 && (
          <Grid container justifyContent="center" paddingX={2}>
            <Grid item md={8} lg={7} flex={1}>
              <Stack
                component="form"
                className="management-update-address__content-container"
              >
                <Typography variant="h3" component="h3">
                  {t(`Gestiones.Nuevos datos`)}
                </Typography>
                <Typography
                  variant="body1"
                  component="span"
                  className="brand-gray"
                >
                  {isAgent ? (
                    <p>
                      <Trans i18nKey="Gestiones.Nuevos datos contacto agente">
                        Link
                        <a
                          className="brand-red"
                          href="mailto:attcanal@totalenergies.com"
                          target="_blank"
                          rel="noreferrer"
                        >
                          content
                        </a>
                      </Trans>
                    </p>
                  ) : (
                    <p>
                      <Trans i18nKey="Gestiones.Nuevos datos contacto">
                        Link
                        <a
                          className="brand-red"
                          href="tel:900926676"
                          target="_blank"
                          rel="noreferrer"
                        >
                          content
                        </a>
                        <a
                          className="brand-red"
                          href="mailto:atencionalcliente@totalenergies.com"
                          target="_blank"
                          rel="noreferrer"
                        >
                          content
                        </a>
                      </Trans>
                    </p>
                  )}
                </Typography>
                <Stack component="div" spacing={2} marginTop={2}>
                  {managementAddressUpdateData.map(
                    ({ id, label, type, required }) => {
                      const isDisabled =
                        id === "municipality"
                          ? isMunicipalityDisabled
                          : id === "city"
                            ? isCityDisabled
                            : false;

                      return (
                        <FormControl
                          required={required}
                          key={id}
                          sx={{
                            display: "flex",
                            gap: "0.5rem",
                            paddingBottom: "1.5rem",
                          }}
                          className={`management-update-address__form-control ${
                            isDisabled ? "disabled" : ""
                          }`}
                        >
                          <FormLabel>
                            <Typography
                              fontWeight={600}
                              color={isDisabled ? theme.palette.grey[300] : ""}
                            >
                              {t(`Gestiones.${label}`)}
                              {required && !isDisabled && (
                                <span style={{ color: "red" }}> *</span>
                              )}
                            </Typography>
                          </FormLabel>

                          <Box
                            component="div"
                            display="flex"
                            flexDirection="row"
                            flex={1}
                            width={"100%"}
                          >
                            {id === "province" ? (
                              <Autocomplete
                                options={provinces}
                                getOptionLabel={(option) => option.name}
                                autoComplete={
                                  newAddressData[id].length > 1 ? true : false
                                }
                                noOptionsText={t("Tablas.No hay coincidencias")}
                                filterOptions={handleFilterOptions}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                onChange={handleProvinceChange}
                                renderOption={(props, option, { selected }) => (
                                  <ListItem
                                    {...props}
                                    key={`${option.name}-${option.id}`}
                                    sx={{
                                      display: "flex",
                                      width: "100%",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    {option.name}
                                    {selected && (
                                      <span className="t-icon-check" />
                                    )}
                                  </ListItem>
                                )}
                                sx={{
                                  maxWidth: { md: "347px" },
                                  width: "100%",
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    sx={{ width: "100%" }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: { boxShadow: "none" },
                                    }}
                                    InputProps={{
                                      ...params.InputProps,
                                      fullWidth: true,
                                      endAdornment: (
                                        <Typography
                                          component="span"
                                          className="t-custom-dropdown t-icon-arrow-down"
                                          data-testid="dropdown-arrow"
                                          sx={{
                                            color: "primary.main",
                                            position: "absolute",
                                            right: "12px",
                                            pointerEvents: "none",
                                          }}
                                        />
                                      ),
                                    }}
                                  />
                                )}
                              />
                            ) : id === "municipality" ? (
                              <Autocomplete
                                options={municipalities}
                                getOptionLabel={(option) => option.name}
                                value={municipalitySelected}
                                disabled={isMunicipalityDisabled}
                                autoComplete={
                                  newAddressData.municipality?.length > 1
                                    ? true
                                    : false
                                }
                                noOptionsText={t("Tablas.No hay coincidencias")}
                                filterOptions={handleFilterOptions}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                onChange={handleMunicipalityChange}
                                renderOption={(props, option, { selected }) => (
                                  <ListItem
                                    {...props}
                                    key={`${option.name}-${option.id}`}
                                    sx={{
                                      display: "flex",
                                      width: "100%",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    {option.name}
                                    {selected && (
                                      <span className="t-icon-check" />
                                    )}
                                  </ListItem>
                                )}
                                sx={{
                                  maxWidth: { md: "347px" },
                                  width: "100%",
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    sx={{ width: "100%" }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: { boxShadow: "none" },
                                    }}
                                    InputProps={{
                                      ...params.InputProps,
                                      fullWidth: true,
                                      endAdornment: (
                                        <Typography
                                          component="span"
                                          className="t-custom-dropdown t-icon-arrow-down"
                                          data-testid="dropdown-arrow"
                                          sx={{
                                            color: "primary.main",
                                            position: "absolute",
                                            right: "12px",
                                            pointerEvents: "none",
                                          }}
                                        />
                                      ),
                                    }}
                                  />
                                )}
                              />
                            ) : id === "city" ? (
                              <Autocomplete
                                options={cities}
                                getOptionLabel={(option) => option.name}
                                value={citySelected}
                                disabled={isCityDisabled}
                                autoComplete={
                                  newAddressData.city &&
                                  newAddressData.city.length > 1
                                    ? true
                                    : false
                                }
                                noOptionsText={t("Tablas.No hay coincidencias")}
                                filterOptions={handleFilterOptions}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                onChange={(event, value) => {
                                  setCitySelected(value);
                                  if (value != null) {
                                    setNewAddressData((prevNewAddressData) => ({
                                      ...prevNewAddressData,
                                      city: value.id.toString(),
                                    }));
                                  }
                                }}
                                renderOption={(props, option, { selected }) => (
                                  <ListItem
                                    {...props}
                                    key={`${option.name}-${option.id}`}
                                    sx={{
                                      display: "flex",
                                      width: "100%",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    {option.name}
                                    {selected && (
                                      <span className="t-icon-check" />
                                    )}
                                  </ListItem>
                                )}
                                sx={{
                                  maxWidth: { md: "347px" },
                                  width: "100%",
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    sx={{ width: "100%" }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: { boxShadow: "none" },
                                    }}
                                    InputProps={{
                                      ...params.InputProps,
                                      fullWidth: true,
                                      endAdornment: (
                                        <Typography
                                          component="span"
                                          className="t-custom-dropdown t-icon-arrow-down"
                                          data-testid="dropdown-arrow"
                                          sx={{
                                            color: "primary.main",
                                            position: "absolute",
                                            right: "12px",
                                            pointerEvents: "none",
                                          }}
                                        />
                                      ),
                                    }}
                                  />
                                )}
                              />
                            ) : id === "streetType" ? (
                              <Autocomplete
                                options={streets}
                                getOptionLabel={(option) => option.full}
                                isOptionEqualToValue={(option, value) =>
                                  option.value === value.value
                                }
                                onChange={(event, value) => {
                                  if (value != null) {
                                    setNewAddressData((prevNewAddressData) => ({
                                      ...prevNewAddressData,
                                      [id]: value.value,
                                    }));
                                  }
                                }}
                                renderOption={(props, option, { selected }) => (
                                  <ListItem
                                    {...props}
                                    sx={{
                                      display: "flex",
                                      width: "100%",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    {option.full}
                                    {selected && (
                                      <span className="t-icon-check" />
                                    )}
                                  </ListItem>
                                )}
                                sx={{
                                  maxWidth: { md: "347px" },
                                  width: "100%",
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    sx={{ width: "100%" }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: { boxShadow: "none" },
                                    }}
                                    InputProps={{
                                      ...params.InputProps,
                                      fullWidth: true,
                                      endAdornment: (
                                        <Typography
                                          component="span"
                                          className="t-custom-dropdown t-icon-arrow-down"
                                          data-testid="dropdown-arrow"
                                          sx={{
                                            color: "primary.main",
                                            position: "absolute",
                                            right: "12px",
                                            pointerEvents: "none",
                                          }}
                                        />
                                      ),
                                    }}
                                  />
                                )}
                              />
                            ) : (
                              <OutlinedInput
                                onChange={({
                                  target: { value },
                                }: React.ChangeEvent<HTMLInputElement>) =>
                                  setNewAddressData((prevNewAddressData) => ({
                                    ...prevNewAddressData,
                                    [id]:
                                      id === "number"
                                        ? value === ""
                                          ? null
                                          : Number(value.split(/,|\./)[0])
                                        : value,
                                  }))
                                }
                                name={id}
                                type={type}
                                sx={{
                                  maxWidth: { md: "347px" },
                                  width: "100%",
                                }}
                              />
                            )}
                            <Tooltip
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth:
                                      renderTooltipBreakpointsStyles("width"),
                                  },
                                },
                              }}
                              title={t(`Gestiones.Tooltip.${id}`)}
                              placement={
                                renderTooltipBreakpointsStyles(
                                  "placement",
                                ) as TooltipPlacement
                              }
                            >
                              <span
                                className={`t-icon-info ${
                                  isDisabled ? "text-disabled" : "brand-red"
                                }`}
                                style={{
                                  paddingLeft: "10px",
                                  cursor: "pointer",
                                  height: "fit-content",
                                  zIndex: "10",
                                  fontSize: `${isSmallViewport ? "20px" : ""}`,
                                }}
                              />
                            </Tooltip>
                          </Box>
                        </FormControl>
                      );
                    },
                  )}
                </Stack>

                <Stack
                  component="div"
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="space-between"
                  alignItems="center"
                  className="management-update-address__actions"
                  sx={{ flexDirection: { xs: "column", md: "row" } }}
                  gap="1rem"
                >
                  <ButtonElement
                    onClick={(event) => {
                      event.preventDefault();
                      window.scrollTo({ top: 0, behavior: "auto" });
                      handleSubmit();
                    }}
                    disabled={isButtonDisabled}
                  >
                    {t("Gestiones.Actualizar dirección de correspondencia")}
                  </ButtonElement>
                  <ButtonElement
                    type="button"
                    onClick={() => setModalOpen(true)}
                    secondary
                  >
                    {t("Cancelar")}
                  </ButtonElement>
                </Stack>
              </Stack>
              <ManagementsCancelProcess
                open={isModalOpen}
                handleClose={() => setModalOpen(false)}
              />
            </Grid>
          </Grid>
        )}
        {[1, 2].includes(step) && (
          <ManagementsRequestState
            state={requestState}
            isLoading={isLoading}
            title={t(requestStateData?.title)}
            description={t(requestStateData?.description)}
          />
        )}
        {[3].includes(step) &&
          managementCupstInfo?.map((request) => (
            <ManagementsRequestState
              state={requestState}
              isLoading={isLoading}
              title={t(requestStateData?.title)}
              description={t(requestStateData?.description)}
              cupsInfo={request}
            />
          ))}
      </Stack>
    </Box>
  );
};

export default ManagementUpdateAddress;
